import { Scene } from "@babylonjs/core/scene";
import { Vector3, Color3, Color4 } from "@babylonjs/core/Maths/math";
import { FreeCamera } from "@babylonjs/core/Cameras/freeCamera";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { HighlightLayer } from "@babylonjs/core/Layers/highlightLayer";
import "@babylonjs/core/Layers/effectLayerSceneComponent";
import "@babylonjs/core/Culling/ray";
import "@babylonjs/core/Rendering/edgesRenderer";
import "@babylonjs/core/Meshes/Builders/cylinderBuilder";
import "@babylonjs/core/Meshes/Builders/boxBuilder";
import "@babylonjs/core/Meshes/Builders/sphereBuilder";
import "@babylonjs/core/Meshes/Builders/torusBuilder";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";

import type { GameScene } from "../engine/SceneManager";
import type { Game } from "../engine/Game";
import { PARTS, partById } from "../parts/PartCatalog";
import type { PartCategory, PartDef } from "../parts/types";
import {
  type PlacedPart,
  type RocketDesign,
  newDesign,
  totalMass,
  stageCount,
  thrustAtStage,
  validateDesign,
  autoFixDesign,
} from "../parts/Rocket";
import { h, clear } from "../ui/dom";
import { showModal } from "../ui/Modal";
import { audio } from "../ui/Audio";
import { TutorialRunner } from "../meta/Tutorial";
import { buildPartMesh } from "../parts/PartMesh";
import { ensurePartIcons, getIcon } from "../parts/IconGen";

const CATEGORY_TITLES: Record<PartCategory, string> = {
  command: "Command",
  propulsion: "Engines & Boosters",
  fuel: "Fuel Tanks",
  aero: "Aerodynamics",
  utility: "Utility",
  structural: "Structural",
};

const SNAP_X = 0.1;
const SNAP_Y = 0.1;
const DRAG_THRESHOLD_METERS = 0.0; // any movement after pointerdown starts the drag

/** Visible buildable region, meters. Matches the grid in setupScene. */
const GRID_X_MIN = -6;
const GRID_X_MAX = 6;
const GRID_Y_MIN = 0;
const GRID_Y_MAX = 18;

type ViewMode = "side" | "3d";

export class BuilderScene implements GameScene {
  readonly babylonScene: Scene;
  private orthoCam!: FreeCamera;
  private arcCam?: ArcRotateCamera;
  private viewMode: ViewMode = "side";
  private highlight!: HighlightLayer;
  private selectionBox?: Mesh;
  private partGroups = new Map<string, TransformNode>();
  private selectedUid?: string;
  private design: RocketDesign;
  private uiEl?: HTMLElement;
  private dragGhost?: HTMLElement;
  private tutorial?: TutorialRunner;

  // Move-existing-part state
  private moving?: {
    uid: string;
    offsetX: number;
    offsetY: number;
    startX: number;
    startY: number;
    entered: boolean;
    pointerId: number;
  };

  constructor(readonly game: Game, arg?: unknown) {
    this.babylonScene = new Scene(game.engine);
    this.babylonScene.clearColor = new Color4(0.03, 0.05, 0.09, 1);
    const existing = (arg as { design?: RocketDesign } | undefined)?.design;
    this.design = existing ?? newDesign("First Rocket");

    // Camera up-front so the render loop always sees an activeCamera.
    this.orthoCam = new FreeCamera("builderCam", new Vector3(0, 5, -20), this.babylonScene);
    this.orthoCam.mode = FreeCamera.ORTHOGRAPHIC_CAMERA;
    this.orthoCam.setTarget(new Vector3(0, 5, 0));
    this.babylonScene.activeCamera = this.orthoCam;
  }

  async enter() {
    this.setupScene();
    (window as any).__builder = this;

    // Render icons in background so UI draws immediately, then refresh palette when ready.
    void ensurePartIcons(96).then(() => this.refreshPaletteIcons());

    this.setupUi();
    this.rebuildAllPartGroups();
    this.updateStats();

    const seen = await this.game.saveManager.kvGet<boolean>("tutorial.builder.seen", false);
    if (!seen) {
      this.tutorial = new TutorialRunner(this.game.uiRoot, [
        { title: "Welcome to the Hangar", body: "Drag parts from the left panel onto the pad to build a rocket." },
        { title: "Start with a Capsule", body: "Every crewed rocket needs a Command module on top so there's someone to fly it.", check: () => this.design.parts.some((p) => partById(p.partId).isControl) },
        { title: "Add a Fuel Tank", body: "Stack a Liquid Tank under the capsule — that's the propellant.", check: () => this.design.parts.some((p) => partById(p.partId).fuelKind === "liquid") },
        { title: "Add an Engine", body: "Attach an engine to the bottom of the tank. No engine, no lift!", check: () => this.design.parts.some((p) => (partById(p.partId).thrust ?? 0) > 0) },
        { title: "Move a part", body: "Tap and drag a part already on the pad to reposition it. Tap the 3D button to orbit around your rocket." },
        { title: "Ready to fly", body: "Hit LAUNCH at the top right when you're happy. If something's wrong we'll tell you why." },
      ]);
      this.tutorial.start();
      await this.game.saveManager.kvSet("tutorial.builder.seen", true);
    }
  }

  private setupScene() {
    this.applyOrthoExtents();

    const hemi = new HemisphericLight("hemi", new Vector3(0, 1, 0), this.babylonScene);
    hemi.intensity = 0.55;
    const key = new DirectionalLight("key", new Vector3(-0.5, -0.9, -0.3), this.babylonScene);
    key.intensity = 1.1;

    // Ground / launchpad
    const pad = MeshBuilder.CreateBox("pad", { width: 10, height: 0.2, depth: 3 }, this.babylonScene);
    pad.position.y = -0.1;
    const padMat = new StandardMaterial("padMat", this.babylonScene);
    padMat.diffuseColor = new Color3(0.12, 0.13, 0.16);
    padMat.specularColor = new Color3(0, 0, 0);
    pad.material = padMat;
    pad.isPickable = false;

    // Grid — rendered BEHIND parts (higher z) and not pickable.
    const gridMat = new StandardMaterial("gridMat", this.babylonScene);
    gridMat.diffuseColor = new Color3(0.18, 0.28, 0.4);
    gridMat.specularColor = new Color3(0, 0, 0);
    gridMat.emissiveColor = new Color3(0.06, 0.12, 0.22);
    gridMat.alpha = 0.7;
    for (let x = -6; x <= 6; x += 1) {
      const g = MeshBuilder.CreateBox("gv", { width: 0.02, height: 18, depth: 0.01 }, this.babylonScene);
      g.position.set(x, 8, 1.0);
      g.material = gridMat;
      g.isPickable = false;
    }
    for (let y = 0; y <= 18; y += 1) {
      const g = MeshBuilder.CreateBox("gh", { width: 14, height: 0.02, depth: 0.01 }, this.babylonScene);
      g.position.set(0, y, 1.0);
      g.material = gridMat;
      g.isPickable = false;
    }
    // Launchpad line — bright y=0 marker
    const base = MeshBuilder.CreateBox("base", { width: 10, height: 0.06, depth: 0.02 }, this.babylonScene);
    base.position.set(0, 0, 0.5);
    const baseMat = new StandardMaterial("baseMat", this.babylonScene);
    baseMat.diffuseColor = new Color3(0.9, 0.45, 0.2);
    baseMat.emissiveColor = new Color3(0.7, 0.3, 0.1);
    baseMat.specularColor = new Color3(0, 0, 0);
    base.material = baseMat;
    base.isPickable = false;

    this.babylonScene.onBeforeRenderObservable.add(() => this.applyOrthoExtents());

    // Selection outline uses HighlightLayer — works across compound meshes
    this.highlight = new HighlightLayer("hl", this.babylonScene);
    this.highlight.innerGlow = false;
    this.highlight.outerGlow = true;
    this.highlight.blurHorizontalSize = 0.6;
    this.highlight.blurVerticalSize = 0.6;
  }

  private applyOrthoExtents() {
    if (this.viewMode !== "side") return;
    const canvas = this.game.canvas;
    const aspect = canvas.clientWidth / Math.max(canvas.clientHeight, 1);
    const halfH = 9;
    const halfW = halfH * aspect;
    this.orthoCam.orthoLeft = -halfW;
    this.orthoCam.orthoRight = halfW;
    this.orthoCam.orthoBottom = -1;
    this.orthoCam.orthoTop = halfH * 2 - 1;
  }

  private canvasToWorld(evX: number, evY: number): { x: number; y: number } {
    const canvas = this.game.canvas;
    const rect = canvas.getBoundingClientRect();
    const nx = (evX - rect.left) / rect.width;
    const ny = (evY - rect.top) / rect.height;
    const l = this.orthoCam.orthoLeft!, r = this.orthoCam.orthoRight!;
    const b = this.orthoCam.orthoBottom!, t = this.orthoCam.orthoTop!;
    const x = l + nx * (r - l);
    const y = t - ny * (t - b);
    return { x, y };
  }

  // ------------------- UI -------------------
  private setupUi() {
    const root = this.game.uiRoot;
    clear(root);

    const ui = h("div", { class: "builder-ui" }, []);

    const top = h("div", { class: "builder-topbar" }, [
      h("div", { style: "display:flex; gap:10px; align-items:center" }, [
        h("button", { class: "btn", onclick: () => this.onBack() }, ["← Menu"]),
        h("input", {
          class: "rocket-name",
          value: this.design.name,
          oninput: (e: Event) => {
            this.design.name = (e.target as HTMLInputElement).value.slice(0, 40);
          },
        }, []),
      ]),
      h("div", { style: "display:flex; gap:8px" }, [
        h("button", {
          class: "btn mobile-only",
          id: "palette-toggle",
          onclick: () => this.togglePalette(),
        }, ["🧩 Parts"]),
        h("button", { class: "btn", id: "view-toggle", onclick: () => this.toggleView() }, ["🎥 3D"]),
        h("button", { class: "btn", onclick: () => this.onSave() }, ["Save"]),
        h("button", { class: "btn primary", onclick: () => this.onLaunch() }, ["Launch 🚀"]),
      ]),
    ]);

    const palette = h("div", { class: "part-palette", id: "builder-palette" }, []);
    this.renderPaletteInto(palette);

    const info = h("div", { class: "part-info panel", id: "builder-info-panel" }, []);

    const bottom = h("div", { class: "builder-bottombar", id: "builder-bottom" }, []);

    ui.appendChild(top);
    ui.appendChild(palette);
    ui.appendChild(info);
    ui.appendChild(bottom);
    root.appendChild(ui);
    this.uiEl = ui;

    const canvas = this.game.canvas;
    canvas.addEventListener("pointerdown", this.onCanvasDown);
    // Touch fallback for environments where pointer events are swallowed by the WebGL
    // canvas (rare, but cheap to cover). `touchstart` fires alongside `pointerdown`;
    // we only act if the pointer path hasn't already.
    canvas.addEventListener("touchstart", this.onCanvasTouchStart, { passive: false });
  }

  private renderPaletteInto(palette: HTMLElement) {
    clear(palette);
    const grouped = new Map<PartCategory, PartDef[]>();
    for (const p of PARTS) {
      if (!grouped.has(p.category)) grouped.set(p.category, []);
      grouped.get(p.category)!.push(p);
    }
    for (const cat of ["command", "propulsion", "fuel", "aero", "structural", "utility"] as PartCategory[]) {
      const list = grouped.get(cat);
      if (!list) continue;
      const sec = h("div", { class: "palette-category" }, [
        h("h3", {}, [CATEGORY_TITLES[cat]]),
        ...list.map((p) => this.renderPaletteItem(p)),
      ]);
      palette.appendChild(sec);
    }
  }

  private refreshPaletteIcons() {
    const palette = document.getElementById("builder-palette");
    if (!palette) return;
    this.renderPaletteInto(palette);
  }

  private renderPaletteItem(p: PartDef): HTMLElement {
    const subtitle =
      p.thrust ? `${(p.thrust / 1000).toFixed(0)} kN thrust`
      : p.fuelCapacity ? `${p.fuelCapacity} kg ${p.fuelKind === "solid" ? "solid" : "liquid"}`
      : p.chuteDragMult ? `Deploys < ${p.chuteDeployAltitude} m`
      : p.isControl ? "Command / Control"
      : p.isDecoupler ? "Separates stages"
      : `${p.mass} kg`;

    const iconData = getIcon(p.id);
    const iconEl = iconData
      ? h("div", { class: "icon" }, [h("img", { src: iconData, alt: p.name, style: "width:100%;height:100%;object-fit:contain;" }, [])])
      : h("div", { class: "icon" }, [p.icon]);

    const el = h("div", { class: "palette-item", "data-part": p.id }, [
      iconEl,
      h("div", { class: "meta" }, [
        h("div", { class: "name" }, [p.name]),
        h("div", { class: "stat" }, [subtitle]),
      ]),
    ]);
    el.addEventListener("pointerdown", (e) => {
      // On mobile the palette is an overlay drawer — close it as the user starts
      // dragging so the snap grid under the cursor is visible.
      this.uiEl?.classList.remove("palette-open");
      this.beginDragFromPalette(p, e);
    });
    return el;
  }

  // ------------------- Palette drawer (mobile) -------------------
  private togglePalette() {
    if (!this.uiEl) return;
    this.uiEl.classList.toggle("palette-open");
  }

  // ------------------- 3D toggle -------------------
  private toggleView() {
    if (this.viewMode === "side") this.enter3dView();
    else this.enterSideView();
  }

  private enter3dView() {
    if (this.viewMode === "3d") return;
    this.viewMode = "3d";

    if (!this.arcCam) {
      this.arcCam = new ArcRotateCamera("arc", -Math.PI / 2.6, Math.PI / 2.4, 22, new Vector3(0, 5, 0), this.babylonScene);
      this.arcCam.lowerRadiusLimit = 6;
      this.arcCam.upperRadiusLimit = 60;
      this.arcCam.wheelPrecision = 2;
      this.arcCam.pinchPrecision = 10;
    }
    this.babylonScene.activeCamera = this.arcCam;
    this.arcCam.attachControl(this.game.canvas, true);

    const btn = document.getElementById("view-toggle");
    if (btn) btn.textContent = "📐 Side";
    audio.play("tap");
  }

  private enterSideView() {
    if (this.viewMode === "side") return;
    this.viewMode = "side";

    if (this.arcCam) this.arcCam.detachControl();
    this.babylonScene.activeCamera = this.orthoCam;
    this.applyOrthoExtents();

    const btn = document.getElementById("view-toggle");
    if (btn) btn.textContent = "🎥 3D";
    audio.play("tap");
  }

  // ------------------- Drag from palette -------------------
  private beginDragFromPalette(part: PartDef, e: PointerEvent) {
    if (this.viewMode !== "side") {
      showToast(this.game.uiRoot, "Switch to Side view to place parts");
      return;
    }
    e.preventDefault();
    const iconData = getIcon(part.id);
    const ghostInner = iconData
      ? h("img", { src: iconData, style: "width:100%;height:100%;object-fit:contain;" }, [])
      : document.createTextNode(part.icon);
    const ghost = h("div", { class: "drag-ghost" }, []);
    ghost.appendChild(ghostInner);
    ghost.style.left = `${e.clientX}px`;
    ghost.style.top = `${e.clientY}px`;
    document.body.appendChild(ghost);
    this.dragGhost = ghost;

    const move = (ev: PointerEvent) => {
      ghost.style.left = `${ev.clientX}px`;
      ghost.style.top = `${ev.clientY}px`;
    };
    const up = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      ghost.remove();
      this.dragGhost = undefined;

      const rect = this.game.canvas.getBoundingClientRect();
      if (ev.clientX < rect.left || ev.clientX > rect.right || ev.clientY < rect.top || ev.clientY > rect.bottom) return;
      const world = this.canvasToWorld(ev.clientX, ev.clientY);
      this.placePart(part, world.x, world.y);
      audio.play("tap");
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  private placePart(part: PartDef, worldX: number, worldY: number) {
    const snap = this.trySnap(part, worldX, worldY);
    let x = snap?.x ?? Math.round(worldX / SNAP_X) * SNAP_X;
    let y = snap?.y ?? Math.round(worldY / SNAP_Y) * SNAP_Y;

    // Clamp to the visible grid so parts are always reachable and visible.
    ({ x, y } = clampToGrid(part, x, y));

    const placed: PlacedPart = {
      uid: crypto.randomUUID(),
      partId: part.id,
      x,
      y,
      rotation: 0,
      mirror: false,
      stage: this.defaultStageFor(part),
      fuel: part.fuelCapacity,
    };
    this.design.parts.push(placed);
    this.buildPartGroup(placed);
    this.selectUid(placed.uid);
    this.updateStats();
  }

  /** When moving an existing part, exclude self from the snap search. */
  private trySnap(part: PartDef, worldX: number, worldY: number, excludeUid?: string): { x: number; y: number } | undefined {
    let best: { dist: number; x: number; y: number } | undefined;
    for (const placed of this.design.parts) {
      if (placed.uid === excludeUid) continue;
      const host = partById(placed.partId);
      for (const node of host.nodes) {
        const nx = placed.x + node.x;
        const ny = placed.y + node.y;
        const d = Math.hypot(nx - worldX, ny - worldY);
        if (d > 2.5) continue;
        if (node.kind === "stack") {
          const mySnap = node.id === "top"
            ? part.nodes.find((n) => n.kind === "stack" && n.y < 0.0001)
            : part.nodes.find((n) => n.kind === "stack" && n.y > 0.0001);
          if (!mySnap) continue;
          const tx = nx - mySnap.x;
          const ty = ny - mySnap.y;
          if (!best || d < best.dist) best = { dist: d, x: tx, y: ty };
        } else {
          const mySnap = part.nodes.find((n) => n.kind === "surface");
          if (!mySnap) continue;
          const tx = nx - mySnap.x;
          const ty = ny - mySnap.y;
          if (!best || d < best.dist) best = { dist: d, x: tx, y: ty };
        }
      }
    }
    return best ? { x: best.x, y: best.y } : undefined;
  }

  private defaultStageFor(part: PartDef) {
    if (part.chuteDragMult) return Math.max(0, stageCount(this.design) - 1) + 1;
    if (part.thrust) return 0;
    if (part.isDecoupler) return 1;
    return 0;
  }

  // ------------------- Groups (compound meshes) -------------------
  private rebuildAllPartGroups() {
    for (const g of this.partGroups.values()) g.dispose();
    this.partGroups.clear();
    for (const p of this.design.parts) this.buildPartGroup(p);
  }

  private buildPartGroup(placed: PlacedPart) {
    const def = partById(placed.partId);
    const group = buildPartMesh(this.babylonScene, def, `build_${placed.uid}`);
    group.metadata = { uid: placed.uid };
    // Tag all descendant meshes so canvas picks can recover the group.
    for (const m of group.getChildMeshes()) m.metadata = { ...(m.metadata ?? {}), uid: placed.uid };
    group.position.set(placed.x, placed.y + def.height / 2, 0);
    this.partGroups.set(placed.uid, group);
  }

  // ------------------- Selection / canvas interaction -------------------
  private selectUid(uid?: string) {
    this.selectedUid = uid;
    this.highlight.removeAllMeshes();
    this.selectionBox?.dispose();
    this.selectionBox = undefined;
    if (uid) {
      const group = this.partGroups.get(uid);
      const placed = this.design.parts.find((p) => p.uid === uid);
      if (group) {
        for (const mesh of group.getChildMeshes()) {
          if (mesh instanceof Mesh) this.highlight.addMesh(mesh as AbstractMesh as Mesh, new Color3(0.3, 0.75, 1));
        }
      }
      if (placed) this.drawSelectionBox(placed);
    }
    this.refreshInfoPanel();
  }

  /** A bright outlined box around the selected part — visible feedback even if HighlightLayer isn't showing. */
  private drawSelectionBox(placed: PlacedPart) {
    const def = partById(placed.partId);
    const box = MeshBuilder.CreateBox(`sel_${placed.uid}`, {
      width: def.halfWidth * 2 + 0.08,
      height: def.height + 0.08,
      depth: def.halfWidth * 2 + 0.08,
    }, this.babylonScene);
    const mat = new StandardMaterial(`selmat_${placed.uid}`, this.babylonScene);
    mat.emissiveColor = Color3.FromHexString("#6dc0ff");
    mat.diffuseColor = Color3.FromHexString("#6dc0ff");
    mat.alpha = 0.18;
    mat.backFaceCulling = false;
    box.material = mat;
    box.isPickable = false;
    box.position.set(placed.x, placed.y + def.height / 2, 0);
    box.enableEdgesRendering();
    box.edgesWidth = 8;
    box.edgesColor = new Color4(0.43, 0.85, 1, 1);
    this.selectionBox = box;
  }

  private onCanvasDown = (e: PointerEvent) => {
    // If the mobile palette drawer is open, a tap on the canvas closes it
    // rather than picking a part — keeps the drawer behaving like a modal.
    if (this.uiEl?.classList.contains("palette-open")) {
      this.uiEl.classList.remove("palette-open");
      return;
    }
    if (this.viewMode !== "side") return; // 3D mode: camera handles input
    spawnClickRipple(e.clientX, e.clientY);
    const canvas = this.game.canvas;
    const rect = canvas.getBoundingClientRect();
    const uid = this.pickPartUid(e.clientX - rect.left, e.clientY - rect.top);
    if (!uid) { this.selectUid(undefined); return; }

    this.selectUid(uid);
    audio.play("tap");

    // Begin potential move (enters drag mode once pointer exceeds DRAG_THRESHOLD)
    const placed = this.design.parts.find((p) => p.uid === uid)!;
    const world = this.canvasToWorld(e.clientX, e.clientY);
    this.moving = {
      uid,
      startX: world.x,
      startY: world.y,
      offsetX: placed.x - world.x,
      offsetY: placed.y - world.y,
      entered: false,
      pointerId: e.pointerId,
    };

    // Capture the pointer on the canvas so move/up events reliably route here
    // even if the cursor leaves the canvas.
    try { canvas.setPointerCapture(e.pointerId); } catch {}

    canvas.addEventListener("pointermove", this.onCanvasMove);
    canvas.addEventListener("pointerup", this.onCanvasUp);
    canvas.addEventListener("pointercancel", this.onCanvasUp);
  };

  /** 2D hit-test in ortho side view: the rocket sits on the x,y plane at z=0.
   *  Uses a small margin so kid-sized finger taps don't need to be pixel-perfect. */
  private pickPartUid(canvasPx: number, canvasPy: number): string | undefined {
    const rect = this.game.canvas.getBoundingClientRect();
    const world = this.canvasToWorld(rect.left + canvasPx, rect.top + canvasPy);

    // Generous pick margin — forgiving for touch input.
    const MARGIN = 0.25;

    let best: { uid: string; area: number } | undefined;
    for (const p of this.design.parts) {
      const def = partById(p.partId);
      const minX = p.x - def.halfWidth - MARGIN;
      const maxX = p.x + def.halfWidth + MARGIN;
      const minY = p.y - MARGIN;
      const maxY = p.y + def.height + MARGIN;
      if (world.x < minX || world.x > maxX || world.y < minY || world.y > maxY) continue;
      const area = (maxX - minX) * (maxY - minY);
      if (!best || area < best.area) best = { uid: p.uid, area };
    }
    return best?.uid;
  }

  private onCanvasMove = (e: PointerEvent) => {
    if (!this.moving || e.pointerId !== this.moving.pointerId) return;
    const world = this.canvasToWorld(e.clientX, e.clientY);
    const dragDist = Math.hypot(world.x - this.moving.startX, world.y - this.moving.startY);

    if (!this.moving.entered && dragDist < DRAG_THRESHOLD_METERS) return;
    this.moving.entered = true;

    const placed = this.design.parts.find((p) => p.uid === this.moving!.uid);
    if (!placed) return;
    const def = partById(placed.partId);

    // Desired target position (with grab offset) — try to snap to another part, else grid.
    const targetX = world.x + this.moving.offsetX;
    const targetY = world.y + this.moving.offsetY;

    const snap = this.trySnap(def, targetX, targetY, placed.uid);
    let nx = snap ? snap.x : Math.round(targetX / SNAP_X) * SNAP_X;
    let ny = snap ? snap.y : Math.round(targetY / SNAP_Y) * SNAP_Y;
    ({ x: nx, y: ny } = clampToGrid(def, nx, ny));
    placed.x = nx;
    placed.y = ny;

    const group = this.partGroups.get(placed.uid);
    if (group) group.position.set(placed.x, placed.y + def.height / 2, 0);
    if (this.selectionBox) this.selectionBox.position.set(placed.x, placed.y + def.height / 2, 0);
  };

  /** Touch-start fallback: some browsers don't synthesise pointer events for every touch.
   *  We synthesise a PointerEvent and route it through the same onCanvasDown path. */
  private onCanvasTouchStart = (e: TouchEvent) => {
    if (this.viewMode !== "side") return;
    if (this.moving) return; // already handled by pointer path
    const t = e.changedTouches[0];
    if (!t) return;
    e.preventDefault();
    const synthetic = new PointerEvent("pointerdown", {
      pointerId: t.identifier + 1,
      clientX: t.clientX,
      clientY: t.clientY,
      bubbles: true,
      pointerType: "touch",
      isPrimary: true,
    });
    this.onCanvasDown(synthetic);
    const onTouchMove = (ev: TouchEvent) => {
      const tt = [...ev.changedTouches].find((c) => c.identifier === t.identifier);
      if (!tt) return;
      ev.preventDefault();
      this.onCanvasMove(new PointerEvent("pointermove", {
        pointerId: t.identifier + 1,
        clientX: tt.clientX,
        clientY: tt.clientY,
        bubbles: true,
      }));
    };
    const onTouchEnd = (ev: TouchEvent) => {
      const tt = [...ev.changedTouches].find((c) => c.identifier === t.identifier);
      if (!tt) return;
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
      this.onCanvasUp(new PointerEvent("pointerup", {
        pointerId: t.identifier + 1,
        clientX: tt.clientX,
        clientY: tt.clientY,
        bubbles: true,
      }));
    };
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("touchcancel", onTouchEnd);
  };

  private onCanvasUp = (e: PointerEvent) => {
    if (this.moving && e.pointerId !== this.moving.pointerId) return;
    const canvas = this.game.canvas;
    canvas.removeEventListener("pointermove", this.onCanvasMove);
    canvas.removeEventListener("pointerup", this.onCanvasUp);
    canvas.removeEventListener("pointercancel", this.onCanvasUp);
    try { canvas.releasePointerCapture(e.pointerId); } catch {}
    if (this.moving?.entered) {
      audio.play("tap");
      this.updateStats();
    }
    this.moving = undefined;
  };

  // ------------------- Info panel / stats -------------------
  private refreshInfoPanel() {
    const panel = document.getElementById("builder-info-panel");
    if (!panel) return;
    clear(panel);

    if (!this.selectedUid) {
      panel.appendChild(h("h3", {}, ["Rocket"]));
      panel.appendChild(h("div", { class: "muted", style: "font-size:13px;line-height:1.45" }, [
        "Tap a part to select.",
        h("br", {}, []),
        "Drag a placed part to move it.",
        h("br", {}, []),
        "Tap 🎥 3D up top to orbit around your rocket.",
      ]));
      const stages = stageCount(this.design);
      if (stages > 0) {
        panel.appendChild(h("h3", { style: "margin-top:14px" }, ["Staging"]));
        const list = h("div", { class: "stage-list" }, []);
        for (let i = 0; i < stages; i++) {
          const count = this.design.parts.filter((p) => p.stage === i).length;
          list.appendChild(h("div", { class: "stage-row" }, [
            h("div", { class: "badge" }, [String(i)]),
            h("div", {}, [`${count} part${count === 1 ? "" : "s"}`]),
          ]));
        }
        panel.appendChild(list);
      }
      return;
    }

    const placed = this.design.parts.find((p) => p.uid === this.selectedUid);
    if (!placed) return;
    const def = partById(placed.partId);

    panel.appendChild(h("h3", {}, [def.name]));
    const stats: [string, string][] = [];
    stats.push(["Mass (dry)", `${def.mass} kg`]);
    if (def.thrust) stats.push(["Thrust", `${(def.thrust / 1000).toFixed(0)} kN`]);
    if (def.ispVac) stats.push(["Isp", `${def.ispAsl ?? def.ispVac}–${def.ispVac} s`]);
    if (def.fuelCapacity) stats.push(["Fuel", `${def.fuelCapacity} kg (${def.fuelKind})`]);
    if (def.chuteDragMult) stats.push(["Deploys", `< ${def.chuteDeployAltitude} m`]);
    if (def.maxTemp) stats.push(["Max temp", `${def.maxTemp} K`]);
    if (def.power) stats.push(["Power", `${def.power} W`]);
    if (def.isControl) stats.push(["Role", "Command / Control"]);

    const table = h("div", {}, []);
    for (const [k, v] of stats) {
      table.appendChild(h("div", { style: "display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid rgba(255,255,255,0.06)" }, [
        h("span", { class: "muted" }, [k]),
        h("span", {}, [v]),
      ]));
    }
    panel.appendChild(table);

    const stageRow = h("div", { style: "margin-top:12px;display:flex;gap:8px;align-items:center" }, [
      h("span", { class: "muted" }, ["Stage"]),
      h("button", { class: "btn", onclick: () => this.changeStage(placed, -1) }, ["−"]),
      h("span", { style: "font-weight:700;font-size:16px;min-width:24px;text-align:center" }, [String(placed.stage)]),
      h("button", { class: "btn", onclick: () => this.changeStage(placed, +1) }, ["+"]),
    ]);
    panel.appendChild(stageRow);

    const delBtn = h("button", { class: "btn danger", style: "margin-top:14px;width:100%", onclick: () => this.deletePart(placed.uid) }, ["Delete Part"]);
    panel.appendChild(delBtn);
  }

  private changeStage(p: PlacedPart, delta: number) {
    p.stage = Math.max(0, p.stage + delta);
    this.refreshInfoPanel();
    this.updateStats();
  }

  private deletePart(uid: string) {
    const idx = this.design.parts.findIndex((p) => p.uid === uid);
    if (idx === -1) return;
    this.design.parts.splice(idx, 1);
    const group = this.partGroups.get(uid);
    group?.dispose();
    this.partGroups.delete(uid);
    this.selectUid(undefined);
    this.updateStats();
  }

  private updateStats() {
    const bottom = document.getElementById("builder-bottom");
    if (!bottom) return;
    clear(bottom);

    const m = totalMass(this.design, partById);
    const stages = stageCount(this.design);
    const thrust0 = thrustAtStage(this.design, 0, partById, 0);
    const twr = m > 0 && thrust0 > 0 ? thrust0 / (m * 9.81) : 0;

    const twrColor = twr >= 1.2 ? "var(--ok)" : twr >= 1.0 ? "var(--accent-strong)" : "var(--warn)";

    const statsEl = h("div", { class: "rocket-stats" }, [
      stat("Mass", `${(m / 1000).toFixed(1)} t`),
      stat("Parts", String(this.design.parts.length)),
      stat("Stages", String(stages)),
      stat("Thrust (S0)", `${(thrust0 / 1000).toFixed(0)} kN`),
      statColored("TWR (S0)", twr.toFixed(2), twrColor),
    ]);

    const v = this.design.parts.length > 0 ? validateDesign(this.design, partById) : null;
    const helperText = v && !v.ok
      ? v.errors[0]
      : twr < 1.0 && this.design.parts.length > 0
        ? "Not enough thrust to lift off. Add a bigger engine or remove mass."
        : this.design.parts.length === 0
          ? "Drag a Capsule, Fuel Tank, and Engine onto the pad to start."
          : "Looking good. Launch when ready.";
    const helper = h("div", { class: "muted", style: "font-size:12px;max-width:360px" }, [helperText]);

    bottom.appendChild(statsEl);
    bottom.appendChild(helper);

    function stat(label: string, val: string) {
      return h("div", { class: "stat" }, [h("span", {}, [label]), h("span", { class: "val" }, [val])]);
    }
    function statColored(label: string, val: string, color: string) {
      return h("div", { class: "stat" }, [
        h("span", {}, [label]),
        h("span", { class: "val", style: `color:${color}` }, [val]),
      ]);
    }
  }

  private async onSave() {
    await this.game.saveManager.saveRocket(this.design);
    showToast(this.game.uiRoot, "Saved");
    audio.play("tap");
  }

  private async onLaunch() {
    const v = validateDesign(this.design, partById);
    if (!v.ok) {
      this.showNotReadyModal(v.errors);
      return;
    }
    if (v.warnings.length > 0) {
      const body = h("div", {}, [
        h("p", {}, ["A few things to watch out for:"]),
        h("ul", { style: "padding-left:20px;margin:8px 0;color:var(--text-dim)" },
          v.warnings.map((msg) => h("li", { style: "margin-bottom:6px;line-height:1.4" }, [msg])),
        ),
      ]);
      const launch = async () => {
        await this.game.saveManager.saveRocket(this.design);
        await this.game.sceneManager.go("flight", { designId: this.design.id });
      };
      showModal(this.game.uiRoot, "Ready — with warnings", body, [
        { label: "Keep Building", onClick: () => {} },
        { label: "Launch Anyway", primary: true, onClick: () => void launch() },
      ]);
      return;
    }
    audio.play("tap");
    await this.game.saveManager.saveRocket(this.design);
    await this.game.sceneManager.go("flight", { designId: this.design.id });
  }

  private async onBack() {
    await this.game.sceneManager.go("menu");
  }

  /** The "Not ready to fly" modal — includes a Fix-it-for-me auto-repair button. */
  private showNotReadyModal(errors: string[]) {
    const body = h("div", {}, [
      h("p", {}, ["Your rocket isn't ready to fly yet:"]),
      h("ul", { style: "padding-left:20px;margin:8px 0" },
        errors.map((msg) => h("li", { style: "margin-bottom:6px;line-height:1.4" }, [msg])),
      ),
      h("p", { class: "muted", style: "margin-top:10px;font-size:13px" }, [
        "Tap ✨ Fix it for me and we'll add or reconnect whatever's missing.",
      ]),
    ]);
    showModal(this.game.uiRoot, "Not ready to fly", body, [
      {
        label: "✨ Fix it for me",
        primary: true,
        onClick: () => this.runAutoFix(),
      },
      { label: "Keep Building", onClick: () => {} },
    ]);
    audio.play("error");
  }

  private runAutoFix() {
    const { changes } = autoFixDesign(this.design, partById);
    this.rebuildAllPartGroups();
    this.selectUid(undefined);
    this.updateStats();
    audio.play("chime");

    const v = validateDesign(this.design, partById);
    if (v.ok) {
      // Ready to fly — summarise what we did and offer to launch.
      const body = h("div", {}, [
        h("p", {}, ["Done! Here's what I changed:"]),
        changes.length > 0
          ? h("ul", { style: "padding-left:20px;margin:8px 0" },
              changes.map((c) => h("li", { style: "margin-bottom:6px;line-height:1.4" }, [c])),
            )
          : h("p", { class: "muted" }, ["(No changes needed.)"]),
        v.warnings.length > 0
          ? h("ul", { style: "padding-left:20px;margin:8px 0;color:var(--text-dim)" },
              v.warnings.map((w) => h("li", { style: "margin-bottom:6px;line-height:1.4" }, [w])),
            )
          : null,
      ]);
      showModal(this.game.uiRoot, "Fixed ✓", body, [
        { label: "Keep Building", onClick: () => {} },
        {
          label: "Launch 🚀",
          primary: true,
          onClick: async () => {
            await this.game.saveManager.saveRocket(this.design);
            await this.game.sceneManager.go("flight", { designId: this.design.id });
          },
        },
      ]);
      return;
    }

    // Still invalid — show remaining errors + what we managed to fix.
    const body = h("div", {}, [
      changes.length > 0
        ? h("div", {}, [
            h("p", {}, ["Here's what I tried:"]),
            h("ul", { style: "padding-left:20px;margin:8px 0" },
              changes.map((c) => h("li", { style: "margin-bottom:4px;line-height:1.4" }, [c])),
            ),
          ])
        : h("p", { class: "muted" }, ["I couldn't apply any automatic fixes."]),
      h("p", { style: "margin-top:10px" }, ["Still blocking launch:"]),
      h("ul", { style: "padding-left:20px;margin:8px 0;color:var(--warn)" },
        v.errors.map((e) => h("li", { style: "margin-bottom:4px;line-height:1.4" }, [e])),
      ),
    ]);
    showModal(this.game.uiRoot, "Partially fixed", body, [
      { label: "Keep Building", primary: true, onClick: () => {} },
    ]);
  }

  leave() {
    this.game.canvas.removeEventListener("pointerdown", this.onCanvasDown);
    this.game.canvas.removeEventListener("touchstart", this.onCanvasTouchStart);
  }

  dispose() {
    this.tutorial?.dispose();
    this.babylonScene.dispose();
    if (this.uiEl) this.uiEl.remove();
    if (this.dragGhost) this.dragGhost.remove();
  }
}

// small helper re-import avoidance
import { showToast } from "../ui/Toast";

/** Brief cyan expanding circle at the click position — confirms the tap registered. */
function spawnClickRipple(clientX: number, clientY: number) {
  const el = document.createElement("div");
  el.className = "click-ripple";
  el.style.left = `${clientX}px`;
  el.style.top = `${clientY}px`;
  document.body.appendChild(el);
  window.setTimeout(() => el.remove(), 500);
}

/** Keep a part's footprint inside the visible grid so it can't go off-screen. */
function clampToGrid(part: PartDef, x: number, y: number): { x: number; y: number } {
  const minX = GRID_X_MIN + part.halfWidth;
  const maxX = GRID_X_MAX - part.halfWidth;
  const minY = GRID_Y_MIN;
  const maxY = GRID_Y_MAX - part.height;
  return {
    x: Math.min(maxX, Math.max(minX, x)),
    y: Math.min(maxY, Math.max(minY, y)),
  };
}
